import * as utils from "common/utils";
import Alert from "components/Alert";
import { HelpTooltip } from "components/Help";
import ProjectedResultsWidget from "../components/ProjectedResultsWidget";
import ProjectedBlocksResults from "../components/ProjectedBlocksResults";
import ProjectedFodderCropRotationsResults from "../components/ProjectedFodderCropRotationsResults";
import ProjectedGrassFedStandardResults from "../components/ProjectedGrassFedStandardResults";
import { GhgEmissions } from "containers/BudgetHome/Overview/Ghg";
import { useOverseerModelRun } from "containers/BudgetHome";

export default function MilkPlatform({ analysis, farm }) {
    const { isLoading, data: milkPlatformResults } = useMilkPlatformResults(analysis);

    if (isLoading)
        return null;

    const hasResults = milkPlatformResults?.farm?.nitrogen !== undefined;
    if (!hasResults) {
        return <Alert type="error" text="There are no milk platform results for this analysis" />
    }

    const fullSystemIsTheMilkPlatform = milkPlatformResults?.farm?.nutrientBudget !== undefined;
    
    const hasDairyGoats = analysis?.enterprises?.find(e => e.type === "DairyGoat") !== undefined;

    const dairyEnterprise = analysis?.enterprises?.find(e => e.type === "Dairy");
    const dairyEnterpriseDiet = milkPlatformResults.farm.animals.diet.byEnterprise.find(d => d.id === dairyEnterprise.id);

    return (
        <>
            {fullSystemIsTheMilkPlatform
                ? <Alert type="warning" text="This analysis only models the milk platform areas of the farm. You will need to go back and enter the non-milk producing areas (if any) in order to see how the milk platform compares to the full farm system." />
                : <Alert type="info" text="The milk platform represents the areas of the farm used for producing milk, specifically - the land designated for grazing and supporting milking cows. By isolating results to the milk platform, this report aims to provide a clearer understanding of the environmental impacts directly associated with milk production, without the influence of other non-milking areas such as support blocks or runoff land. Please refer to the animal reports for a monthly breakdown of grass-fed status by enterprise." />
            }
            <div className="HeroPanel u-mt-md u-page-break u-print-border">
                <div className="HeroPanel-title">Milk platform {hasDairyGoats ? "(excl. dairy goats)" : undefined}</div>
                <div className="HeroValue u-pt-lg u-flexWrap">
                    <div className="HeroValue-item u-mb-lg">
                        <h5>
                            Farm system type
                            <HelpTooltip tip={milkPlatformResults.farm.animals.production.dairy.farmSystemType.description} />
                        </h5>
                        <div className="u-flex u-flexColumn">
                            <span id="milk_platform_ghg" className="h1">
                                System {milkPlatformResults.farm.animals.production.dairy.farmSystemType.value}
                            </span>
                        </div>
                        <div className="u-flex u-flexColumn">
                            <span id="milk_platform_ghgha" className="h2">
                                {utils.numeric.formatAllowZero(dairyEnterpriseDiet.percentageOfDietFromImportedFeed.value, 1)} <small>{dairyEnterpriseDiet.percentageOfDietFromImportedFeed.unit.notation} imported feed</small>
                            </span>
                        </div>
                    </div>
                    <div className="HeroValue-item u-mb-lg">
                        <h5>
                            Area
                            <HelpTooltip tip={milkPlatformResults.farm.farmArea.description} />
                        </h5>
                        <div className="u-flex u-flexColumn">
                            <span id="milk_platform_ghg" className="h1">
                                {utils.numeric.format(milkPlatformResults.farm.farmArea.value, 1)} <small>{milkPlatformResults.farm.farmArea.unit.notation}</small>
                            </span>
                        </div>
                        <div className="u-flex u-flexColumn">
                            <span id="milk_platform_ghgha" className="h2">
                                {utils.numeric.format(milkPlatformResults.farm.percentageOfTotalFarmArea.value, 1)} <small>{milkPlatformResults.farm.percentageOfTotalFarmArea.unit.notation} of total farm area</small>
                            </span>
                        </div>
                    </div>
                    <div className="HeroValue-item u-mb-lg">
                        <h5>
                            Total milk solids
                            <HelpTooltip tip={milkPlatformResults.farm.animals.production.dairy.milkSolids.description} />
                        </h5>
                        <div className="u-flex u-flexColumn">
                            <span id="milk_platform_ghg" className="h1">
                                {utils.numeric.format(milkPlatformResults.farm.animals.production.dairy.milkSolids.value)} <small>{milkPlatformResults.farm.animals.production.dairy.milkSolids.unit.notation}</small>
                            </span>
                        </div>
                        <div className="u-flex u-flexColumn">
                            <span id="milk_platform_ghgha" className="h2">
                                &nbsp;
                            </span>
                        </div>
                    </div>
                    <ProjectedGrassFedStandardResults platformResults={milkPlatformResults?.farm} />
                </div>
                <ProjectedResultsWidget title="Milk platform" platformResults={milkPlatformResults?.farm} />
            </div>
            <ProjectedBlocksResults blocksResults={milkPlatformResults?.blocks} />
            <ProjectedFodderCropRotationsResults fodderCropRotationsResults={milkPlatformResults?.fodderCropRotations} />
            <br />
            <GhgEmissions ghgResults={milkPlatformResults?.farm?.ghg} title="GHG emissions for the milk platform" />
        </>
    );
}

function useMilkPlatformResults(analysis) {
    const { isLoading, data } = useOverseerModelRun(analysis, "MilkPlatform");

    return {
        isLoading,
        data: data?.modelResults,
    }
}
